import { Button } from 'primereact/button';
import React, { useCallback, useEffect, useRef, useState } from 'react';
// TODO: move to '@/ui/rich-text' — react-quill is being confined there
// so the editor can be swapped in one place.
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import styled from 'styled-components';

const EditableContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DisplayContent = styled.div`
  padding: 4px 8px;
  border-radius: 4px;
`;

const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EditInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px;
  border: 2px solid var(--green-main);
  border-radius: 4px;
  font-family: var(--font-family);
  font-size: var(--small-font-size);
  line-height: 1.5;
  color: var(--black-main);
  background: transparent;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: var(--green-main);
    box-shadow: 0 0 0 2px var(--green-success-bg-focus);
  }
`;

const StyledReactQuill = styled(ReactQuill)`
  width: 100%;
  border: none !important;

  &:hover {
    border: none !important;
  }

  .ql-container {
    border: 2px solid var(--green-main) !important;
    border-radius: 4px;
    font-family: var(--font-family);
    font-size: var(--small-font-size);
    background: transparent !important;

    &.ql-bubble {
      border: 2px solid var(--green-main) !important;
    }

    &:hover,
    &:hover.ql-bubble {
      border: 2px solid var(--green-main) !important;
      box-shadow: none !important;
    }

    &:focus,
    &:focus-within {
      border-color: var(--green-main) !important;
      border: 2px solid var(--green-main) !important;
      box-shadow: 0 0 0 2px var(--green-success-bg-focus) !important;
    }
  }

  .ql-editor {
    padding: 8px;
    min-height: 80px;
    color: var(--black-main);
    font-family: var(--font-family);
    font-size: var(--small-font-size);
    line-height: 1.5;
    background: transparent;

    &:hover {
      border: none !important;
    }

    &.ql-blank::before {
      color: var(--gray-darker);
      font-style: italic;
    }

    p {
      margin: 0 0 8px 0;

      &:last-child {
        margin-bottom: 0;
      }
    }

    strong,
    b {
      font-weight: 600;
    }

    ul,
    ol {
      margin: 8px 0;
      padding-left: 20px;
    }

    li {
      margin: 4px 0;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ConfirmButton = styled(Button)`
  &.p-button {
    background: var(--green-main);
    color: var(--white-main);
    border: 1px solid var(--green-main);
    border-radius: 4px;
    padding: 6px 12px;
    font-size: var(--xxsmall-font-size);
    height: auto;
    min-width: 50px;

    &:hover:not(:disabled) {
      background: #4caf50;
      border-color: #4caf50;
    }
  }
`;

const CancelButton = styled(Button)`
  &.p-button {
    background: var(--white-main);
    color: var(--black-main);
    border: 1px solid var(--gray-dark);
    border-radius: 4px;
    padding: 6px 12px;
    font-size: var(--xxsmall-font-size);
    height: auto;
    min-width: 50px;

    &:hover:not(:disabled) {
      background: var(--beige-main);
      border-color: var(--gray-dark);
    }
  }
`;

export type InlineEditableFieldProps = {
  value: string | null | undefined;
  onSave: (value: string) => void;
  onCancel: () => void;
  isHtml?: boolean;
  placeholder?: string;
  children?: React.ReactNode; // Display content when not editing
  className?: string;
  renderButtonsInHeader?: boolean; // If true, buttons will be rendered separately
  onEditingStateChange?: (isEditing: boolean) => void; // Callback when editing state changes
  onHandlersReady?: (handlers: {
    onSave: () => void;
    onCancel: () => void;
  }) => void; // Callback with save/cancel handlers
  isEditing?: boolean; // External control of editing state
};

export type InlineEditableFieldButtonsProps = {
  onSave: () => void;
  onCancel: () => void;
};

export const InlineEditableFieldButtons: React.FC<
  InlineEditableFieldButtonsProps
> = ({ onSave, onCancel }) => {
  return (
    <ButtonContainer>
      <CancelButton label="No" onClick={onCancel} aria-label="Cancel edit" />
      <ConfirmButton label="Yes" onClick={onSave} aria-label="Confirm edit" />
    </ButtonContainer>
  );
};

export const InlineEditableField: React.FC<InlineEditableFieldProps> = ({
  value,
  onSave,
  onCancel,
  isHtml = false,
  placeholder = '',
  children,
  className,
  renderButtonsInHeader = false,
  onEditingStateChange,
  onHandlersReady,
  isEditing: externalIsEditing,
}) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing =
    externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const [editValue, setEditValue] = useState(value || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const quillRef = useRef<ReactQuill>(null);

  useEffect(() => {
    if (onEditingStateChange) {
      onEditingStateChange(isEditing);
    }
  }, [isEditing, onEditingStateChange]);

  const handleSave = useCallback(() => {
    onSave(editValue);

    if (externalIsEditing === undefined) {
      setInternalIsEditing(false);
    }
  }, [editValue, isHtml, onSave, externalIsEditing]);

  const handleCancel = useCallback(() => {
    setEditValue(value || '');
    if (externalIsEditing === undefined) {
      setInternalIsEditing(false);
    }
    onCancel();
  }, [value, onCancel, externalIsEditing]);

  // Expose handlers when editing starts
  useEffect(() => {
    if (isEditing && renderButtonsInHeader && onHandlersReady) {
      onHandlersReady({
        onSave: handleSave,
        onCancel: handleCancel,
      });
    }
  }, [
    isEditing,
    renderButtonsInHeader,
    onHandlersReady,
    handleSave,
    handleCancel,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      }
    },
    [handleCancel, handleSave],
  );

  if (isEditing) {
    return (
      <EditableContainer className={className}>
        <EditContainer>
          {isHtml ? (
            <StyledReactQuill
              key={`quill-${isEditing}`}
              ref={quillRef}
              value={editValue}
              onChange={setEditValue}
              placeholder={placeholder}
              modules={{
                toolbar: false, // No toolbar for inline editing
                clipboard: {
                  matchVisual: false,
                },
              }}
              theme="bubble"
            />
          ) : (
            <EditInput
              ref={textareaRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              rows={3}
            />
          )}
          {!renderButtonsInHeader && (
            <ButtonContainer>
              <CancelButton
                label="No"
                onClick={handleCancel}
                aria-label="Cancel edit"
              />
              <ConfirmButton
                label="Yes"
                onClick={handleSave}
                aria-label="Confirm edit"
              />
            </ButtonContainer>
          )}
        </EditContainer>
      </EditableContainer>
    );
  }

  return (
    <EditableContainer className={className}>
      <DisplayContent>
        {children || value || (
          <span style={{ fontStyle: 'italic', color: 'var(--gray-darker)' }}>
            None
          </span>
        )}
      </DisplayContent>
    </EditableContainer>
  );
};
